import { useEffect } from "react";

import { faBadgeCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import invariant from "ts-invariant";

import type { Therapist } from "api/models/Therapist";
import { useApiClient } from "api/useApiClient";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { removeAuthCookies } from "utils/cookie";
import { userIsBillingAdmin, userIsPartner, userIsPhysician } from "utils/profile/profileHelper";

import { completeOnboarding } from "../queries/completeOnboarding";

import { IconContainer } from "./styles";

export const Complete = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useApiClient();
  const { profile, setProfile } = useProfileContext();

  const isBillingAdmin = userIsBillingAdmin(profile);
  const isPartner = userIsPartner(profile);
  const isPhysician = userIsPhysician(profile);

  invariant(profile);

  const onboard = () => {
    completeOnboarding({ profileId: profile.id, isBillingAdmin, isPartner, isPhysician })
      .then(() => {
        client
          .getAuthenticatedUser()
          .then(response => {
            const { data } = response;

            setProfile(data as unknown as Therapist);
            if (data.state === "ready") {
              navigate("/dashboard");
            }
          })
          .catch(() => {
            removeAuthCookies();
          });
      })
      .catch(error => {
        if (error?.response?.status && error.response.status === 406) {
          navigate("/dashboard");
        }
      });
  };

  useEffect(() => {
    if (isPartner || isPhysician) {
      onboard();
    }
  }, []);

  return isPartner || isPhysician ? (
    <></>
  ) : (
    <>
      <Stack sx={{ alignItems: "center" }}>
        <IconContainer>
          <FontAwesomeIcon icon={faBadgeCheck} fontSize="32px" />
        </IconContainer>

        <Typography variant="h2" mt="16px" mb="8px">
          {t("onboarding.4.title")}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {t("onboarding.4.body")}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={2} justifyContent="space-between">
        <BaseButton
          dataTestID="onboarding-complete-back-btn"
          text={t("buttons.back")}
          onClick={() => navigate("/onboarding/about-you")}
          variant="tertiary"
          icon="arrow-left"
          iconPlacement="start"
          style={{
            flexBasis: "auto",
          }}
        />

        <BaseButton
          dataTestID="onboarding-complete-page-next-btn"
          text={t("onboarding.4.complete")}
          onClick={onboard}
          icon="check"
          iconPlacement="end"
          style={{
            flexBasis: "auto",
          }}
        />
      </Stack>
    </>
  );
};
